import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { MessageThread } from "@/components/ProjectMessages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileChips } from "@/components/shared";
import { toast } from "sonner";
import { MessagesSquare, ArrowLeft, Building2, Contact, FolderOpen } from "lucide-react";

const fmtShort = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const convKey = (c) => `${c.kind}-${c.request_id || c.project_id || c.customer_id}-${c.supplier_id || ""}`;
const convEndpoint = (c) =>
  c.kind === "supplier" ? `/requests/${c.request_id}/messages/${c.supplier_id}`
  : c.kind === "customer_project" ? `/projects/${c.project_id}/messages`
  : `/customers/${c.customer_id}/messages`;

export default function ChatsPage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("supplier");
  const [selected, setSelected] = useState(null);
  const [filesOpen, setFilesOpen] = useState(false);
  const [filesData, setFilesData] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);

  const openFiles = async () => {
    setFilesOpen(true);
    setFilesLoading(true);
    try {
      const params = {};
      if (selected.request_id) params.request_id = selected.request_id;
      if (selected.project_id) params.project_id = selected.project_id;
      const { data: d } = await api.get("/chat-files", { params });
      setFilesData(d);
    } catch (e) {
      toast.error(apiError(e));
      setFilesData({ request_files: [], project_files: [], project_name: null });
    } finally {
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    api.get("/chats").then((r) => setData(r.data)).catch(() => setData({ conversations: [], unread: {} }));
  }, []);

  if (!data) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const convs = data.conversations.filter((c) => (tab === "supplier" ? c.kind === "supplier" : c.kind !== "supplier"));
  const supUnread = data.conversations.filter((c) => c.kind === "supplier").reduce((a, c) => a + c.unread, 0);
  const custUnread = data.conversations.filter((c) => c.kind !== "supplier").reduce((a, c) => a + c.unread, 0);

  const select = (c) => {
    setSelected(c);
    if (c.unread > 0) {
      setData((d) => ({ ...d, conversations: d.conversations.map((x) => (convKey(x) === convKey(c) ? { ...x, unread: 0 } : x)) }));
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="chats-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yönetim</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Sohbetler</h1>
        <p className="text-sm text-slate-500 mt-2">Tedarikçi ve müşterilerle tüm mesajlaşmalarınız tek yerde.</p>
      </div>

      <div className="flex gap-1">
        {[
          { key: "supplier", label: "Tedarikçiler", icon: Building2, unread: supUnread },
          { key: "customer", label: "Müşteriler", icon: Contact, unread: custUnread },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setSelected(null); }}
            data-testid={`chats-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm transition-colors duration-200 ${
              tab === t.key ? "bg-slate-900 text-white font-semibold" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-900"
            }`}
          >
            <t.icon size={14} /> {t.label}
            {t.unread > 0 && <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 leading-none rounded-full">{t.unread}</span>}
          </button>
        ))}
      </div>

      <div className="md:grid md:grid-cols-[340px,1fr] md:gap-6 items-start">
        <div className={`border border-slate-200 bg-white md:max-h-[70vh] md:overflow-y-auto ${selected ? "hidden md:block" : ""}`} data-testid="chats-list">
          {convs.length === 0 ? (
            <p className="text-sm text-slate-500 p-6" data-testid="no-chats">Henüz mesajlaşma yok.</p>
          ) : (
            convs.map((c) => {
              const active = selected && convKey(selected) === convKey(c);
              return (
                <button
                  key={convKey(c)}
                  onClick={() => select(c)}
                  data-testid={`chat-item-${convKey(c)}`}
                  className={`w-full text-left px-4 py-3.5 border-b border-slate-100 last:border-0 transition-colors duration-200 ${
                    active ? "bg-slate-900 text-white" : "hover:bg-slate-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-semibold text-sm truncate ${active ? "text-white" : "text-slate-900"}`}>{c.title}</span>
                    <span className={`text-[11px] shrink-0 ${active ? "text-slate-400" : "text-slate-400"}`}>{fmtShort(c.last_at)}</span>
                  </div>
                  <div className={`text-xs truncate mt-0.5 ${active ? "text-slate-300" : "text-slate-500"}`}>{c.subtitle}</div>
                  <div className="flex items-center justify-between gap-2 mt-1.5">
                    <span className={`text-xs truncate ${active ? "text-slate-400" : c.unread > 0 ? "text-slate-900 font-medium" : "text-slate-400"}`}>
                      {c.last_role === "admin" ? "Siz: " : ""}{c.last_text || "📎 Dosya gönderildi"}
                    </span>
                    {c.unread > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 leading-none rounded-full shrink-0" data-testid={`chat-unread-${convKey(c)}`}>
                        {c.unread}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className={selected ? "" : "hidden md:block"}>
          {selected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <button onClick={() => setSelected(null)} className="md:hidden text-slate-500 hover:text-slate-900" data-testid="chat-back-button">
                  <ArrowLeft size={18} />
                </button>
                <div>
                  <div className="font-semibold text-sm" data-testid="chat-thread-title">{selected.title}</div>
                  <div className="text-xs text-slate-500">{selected.subtitle}</div>
                </div>
                {(selected.request_id || selected.project_id) && (
                  <Button variant="outline" size="sm" onClick={openFiles} data-testid="chat-project-files-button" className="ml-auto rounded-none h-8 border-slate-200 hover:bg-slate-100 gap-1.5 text-xs font-semibold transition-colors duration-200">
                    <FolderOpen size={13} /> Proje Dosyaları
                  </Button>
                )}
                {selected.kind === "supplier" ? (
                  <Badge variant="outline" className={`rounded-none text-[10px] bg-blue-500/10 text-blue-600 border-blue-500/20 ${selected.request_id || selected.project_id ? "" : "ml-auto"}`}>Tedarikçi</Badge>
                ) : (
                  <Badge variant="outline" className={`rounded-none text-[10px] bg-emerald-500/10 text-emerald-600 border-emerald-500/20 ${selected.request_id || selected.project_id ? "" : "ml-auto"}`}>Müşteri</Badge>
                )}
              </div>
              <MessageThread
                key={convKey(selected)}
                endpoint={convEndpoint(selected)}
                myRole="admin"
                title={selected.kind === "supplier" ? "Tedarikçi Mesajları" : "Müşteri Mesajları"}
                emptyText="Henüz mesaj yok."
                placeholder="Mesajınızı yazın..."
              />
            </div>
          ) : (
            <div className="border border-slate-200 bg-white p-12 text-center text-slate-400 flex flex-col items-center gap-3" data-testid="no-chat-selected">
              <MessagesSquare size={28} />
              <p className="text-sm">Görüntülemek için soldan bir sohbet seçin.</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={filesOpen} onOpenChange={setFilesOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-xl" data-testid="chat-files-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
              <FolderOpen size={18} className="text-slate-600" /> Proje Dosyaları{filesData?.project_name ? ` — ${filesData.project_name}` : ""}
            </DialogTitle>
          </DialogHeader>
          {filesLoading ? (
            <p className="text-sm text-slate-500 py-6 text-center">Yükleniyor...</p>
          ) : filesData && (filesData.request_files.length > 0 || filesData.project_files.length > 0) ? (
            <div className="space-y-5">
              {filesData.request_files.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Talep Ekleri (Tasarımlar)</div>
                  <FileChips files={filesData.request_files} />
                </div>
              )}
              {filesData.project_files.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Proje Dosyaları</div>
                  <FileChips files={filesData.project_files} />
                </div>
              )}
              <p className="text-[11px] text-slate-400">Dosyaya tıklayarak önizleyebilir, indirme ikonuyla indirebilirsiniz.</p>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="chat-files-empty">Bu projeye ekli dosya bulunmuyor.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
